import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Star, 
  MapPin, 
  Globe, 
  Heart,
  MessageCircle,
  Award,
  ArrowLeft,
  BookOpen,
  Clock,
  CheckCircle,
  DollarSign,
  Calendar,
  Briefcase,
  Users,
  X
} from 'lucide-react';
import { api } from '../services/api';
import LazyImage from '../components/ui/LazyImage';

const TutorDetailPage = () => {
  const { id } = useParams(); // Get tutor ID from URL
  const navigate = useNavigate();
  
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    loadTutor();
  }, [id]);

  // Set page title when tutor loads
  useEffect(() => {
    if (tutor) {
      document.title = `${tutor.name} - Tutor Profile`;
    }
  }, [tutor]);

  const loadTutor = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch tutor by ID
      const tutorData = await api.tutors.getById(id);
      
      if (!tutorData) {
        setError('Tutor not found');
        return;
      }

      // Check if tutor is rejected
      if (tutorData.status === 'rejected') {
        setError('This tutor profile is not available');
        return;
      }
      
      // Format tutor data
      const formattedTutor = {
        id: tutorData.$id,
        name: `${tutorData.firstName} ${tutorData.lastName}`.toUpperCase(),
        code: `${tutorData.firstName[0]}${tutorData.lastName[0]}-${tutorData.$id.slice(-3)}`.toUpperCase(),
        title: tutorData.curriculums?.[0]?.toUpperCase() || 'CHINESE TUTOR',
        subtitle: tutorData.curriculums?.[1]?.toUpperCase() || '',
        photo: tutorData.profilePhotoId 
          ? `${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutorData.profilePhotoId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`
          : null,
        rating: 4.8,
        reviewCount: Math.floor(Math.random() * 150) + 20,
        hourlyRate: tutorData.hourlyRateOnline || 400,
        languages: extractLanguages(tutorData),
        experience: parseInt(tutorData.yearsExperience) || 0,
        specialties: tutorData.curriculums || [],
        location: tutorData.baseDistricts?.[0]?.toUpperCase() || 'HONG KONG',
        available: tutorData.locationPreference !== 'unavailable',
        topRated: parseInt(tutorData.yearsExperience) >= 8,
        willTravel: tutorData.locationPreference === 'in_person_only' || tutorData.locationPreference === 'both',
        bio: tutorData.tutorBio || 'Experienced Chinese language tutor dedicated to helping students achieve their language learning goals.',
        expertise: tutorData.teachingStyles || [],
        teachingStyle: tutorData.teachingStyles?.[0]?.toUpperCase() || 'PERSONALIZED',
        availability: formatAvailability(tutorData.availability),
        studentQuote: 'Great tutor! Very patient and knowledgeable.',
        studentName: 'STUDENT',
        originalData: tutorData
      };
      
      setTutor(formattedTutor);
      
      // Check if saved (from localStorage)
      const savedTutors = JSON.parse(localStorage.getItem('savedTutors') || '[]');
      setIsSaved(savedTutors.includes(id));
      
    } catch (err) {
      console.error('Error loading tutor:', err);
      setError('Failed to load tutor profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const extractLanguages = (tutorData) => {
    const languages = new Set();
    
    if (tutorData.curriculums) {
      tutorData.curriculums.forEach(curr => {
        if (curr.includes('mandarin') || curr.includes('hsk') || curr.includes('ap_chinese')) {
          languages.add('Mandarin');
        }
        if (curr.includes('cantonese') || curr.includes('hkdse')) {
          languages.add('Cantonese');
        }
      });
    }
    
    if (languages.size === 0) {
      languages.add('Mandarin');
    }
    
    languages.add('English');
    
    return Array.from(languages);
  };

  const formatAvailability = (availability) => {
    if (!availability) return 'FLEXIBLE';
    
    let avail = availability;
    if (typeof availability === 'string') {
      try {
        avail = JSON.parse(availability);
      } catch (e) {
        return 'FLEXIBLE';
      }
    }
    
    const availableSlots = Object.values(avail).filter(v => v === true).length;
    
    if (availableSlots === 0) return 'LIMITED';
    if (availableSlots > 20) return 'HIGHLY FLEXIBLE';
    return 'FLEXIBLE';
  };

  const toggleSaved = () => {
    const savedTutors = JSON.parse(localStorage.getItem('savedTutors') || '[]');
    
    if (isSaved) {
      const updated = savedTutors.filter(tutorId => tutorId !== id);
      localStorage.setItem('savedTutors', JSON.stringify(updated));
      setIsSaved(false);
    } else {
      savedTutors.push(id);
      localStorage.setItem('savedTutors', JSON.stringify(savedTutors));
      setIsSaved(true);
    }
  };

  const handleBooking = () => {
    // TODO: Implement booking flow
    alert('Opening booking for ' + tutor.name);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-800 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 uppercase tracking-widest text-sm">LOADING PROFILE...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <X size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 uppercase tracking-widest">ERROR</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/tutors')}
            className="px-6 py-3 bg-white text-black uppercase text-xs tracking-widest font-semibold hover:bg-gray-200 transition-colors"
          >
            BACK TO TUTORS
          </button>
        </div>
      </div>
    );
  }

  // No tutor found
  if (!tutor) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Users size={48} className="text-gray-700 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 uppercase tracking-widest">TUTOR NOT FOUND</h2>
          <p className="text-gray-400 mb-6">This tutor profile does not exist.</p>
          <button
            onClick={() => navigate('/tutors')}
            className="px-6 py-3 bg-white text-black uppercase text-xs tracking-widest font-semibold hover:bg-gray-200 transition-colors"
          >
            BACK TO TUTORS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black border-b border-gray-900">
        <div className="container mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/tutors')}
              className="text-gray-500 hover:text-white transition-colors uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              ALL TUTORS
            </button>
            
            <div className="text-center flex-1">
              <h1 className="text-xl md:text-2xl font-bold tracking-widest">TUTOR PROFILE</h1>
            </div>

            <button
              onClick={toggleSaved}
              className={`p-2 transition-colors ${
                isSaved ? 'text-red-500' : 'text-gray-500 hover:text-white'
              }`}
            >
              <Heart size={20} fill={isSaved ? 'currentColor' : 'none'} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <div className="grid md:grid-cols-5 gap-8 mb-12">
              {/* Photo - 2 columns */}
              <div className="md:col-span-2">
                <div className="aspect-[3/4] bg-gray-900 rounded-lg overflow-hidden">
                  {tutor.photo ? (
                    <img 
                      src={tutor.photo} 
                      alt={tutor.name}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-900 via-gray-900 to-gray-800 flex items-center justify-center">
                      <div className="text-9xl font-bold text-gray-700">
                        {tutor.name.split(' ')[0][0]}{tutor.name.split(' ')[1][0]}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Info - 3 columns */}
              <div className="md:col-span-3 flex flex-col justify-between">
                {/* Top Section */}
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-2">
                      {tutor.topRated && (
                        <div className="px-3 py-1 bg-yellow-500 bg-opacity-10 border border-yellow-500 border-opacity-30 text-xs text-yellow-500 uppercase tracking-wider">
                          TOP RATED
                        </div>
                      )}
                      {tutor.available && (
                        <div className="px-3 py-1 bg-green-500 bg-opacity-10 border border-green-500 border-opacity-30 text-xs text-green-500 uppercase tracking-wider">
                          AVAILABLE
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-600 tracking-widest font-mono">
                      {tutor.code}
                    </span>
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
                    {tutor.name}
                  </h1>
                  <p className="text-sm text-gray-500 uppercase tracking-widest mb-8">
                    {tutor.title} {tutor.subtitle && `— ${tutor.subtitle}`}
                  </p>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-6 mb-8">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-2">
                        <Star size={16} className="text-yellow-500 fill-yellow-500" />
                        <span className="text-2xl font-bold">{tutor.rating}</span>
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        ({tutor.reviewCount})
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold mb-2">
                        {tutor.experience}Y
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        EXPERIENCE
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold mb-2">
                        ${tutor.hourlyRate}
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        PER HOUR
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold mb-2">
                        {tutor.languages.length}
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        LANGUAGES
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleBooking}
                    className="flex-1 py-4 bg-white text-black border border-white hover:bg-transparent hover:text-white transition-all uppercase text-xs tracking-widest font-semibold"
                  >
                    BOOK TRIAL SESSION
                  </button>
                  <button
                    className="flex-1 py-4 border border-white text-white hover:bg-white hover:text-black transition-all uppercase text-xs tracking-widest font-semibold"
                  >
                    <MessageCircle size={14} className="inline mr-2" />
                    SEND MESSAGE
                  </button>
                </div>
              </div>
            </div>

            {/* Content Sections */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Main Column - 2/3 width */}
              <div className="md:col-span-2 space-y-12">
                {/* About */}
                <section>
                  <h2 className="text-xs text-gray-600 uppercase tracking-widest mb-6 pb-3 border-b border-gray-900">
                    ABOUT
                  </h2>
                  <p className="text-gray-300 leading-relaxed text-lg">
                    {tutor.bio}
                  </p>
                </section>

                {/* Expertise */}
                {tutor.expertise.length > 0 && (
                  <section>
                    <h2 className="text-xs text-gray-600 uppercase tracking-widest mb-6 pb-3 border-b border-gray-900">
                      TEACHING APPROACH
                    </h2>
                    <div className="space-y-4">
                      {tutor.expertise.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-4">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-300 text-base">{item}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Specialties */}
                <section>
                  <h2 className="text-xs text-gray-600 uppercase tracking-widest mb-6 pb-3 border-b border-gray-900">
                    CURRICULUM SPECIALTIES
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {tutor.specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-white bg-opacity-5 border border-white border-opacity-10 text-sm uppercase tracking-wider hover:bg-opacity-10 transition-colors"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </section>

                {/* Testimonial */}
                {tutor.studentQuote && (
                  <section>
                    <h2 className="text-xs text-gray-600 uppercase tracking-widest mb-6 pb-3 border-b border-gray-900">
                      STUDENT FEEDBACK
                    </h2>
                    <div className="border-l-2 border-purple-500 border-opacity-30 pl-6 py-2">
                      <p className="text-gray-300 italic text-lg mb-4">
                        "{tutor.studentQuote}"
                      </p>
                      <p className="text-xs text-gray-600 uppercase tracking-wider">
                        — {tutor.studentName}
                      </p>
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar - 1/3 width */}
              <div className="md:col-span-1">
                <div className="bg-gray-900 bg-opacity-50 border border-gray-800 rounded-lg p-6 sticky top-24">
                  <h2 className="text-xs text-gray-600 uppercase tracking-widest mb-6 pb-3 border-b border-gray-800">
                    DETAILS
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Location */}
                    <div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 uppercase tracking-wider mb-2">
                        <MapPin size={14} />
                        LOCATION
                      </div>
                      <div className="text-sm text-white">
                        {tutor.location}
                      </div>
                      {tutor.willTravel && (
                        <div className="text-xs text-green-400 mt-1">
                          • Travels to students
                        </div>
                      )}
                    </div>

                    {/* Languages */}
                    <div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 uppercase tracking-wider mb-2">
                        <Globe size={14} />
                        LANGUAGES
                      </div>
                      <div className="text-sm text-white">
                        {tutor.languages.join(', ')}
                      </div>
                    </div>

                    {/* Teaching Style */}
                    <div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 uppercase tracking-wider mb-2">
                        <BookOpen size={14} />
                        TEACHING STYLE
                      </div>
                      <div className="text-sm text-white">
                        {tutor.teachingStyle}
                      </div>
                    </div>

                    {/* Availability */}
                    <div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 uppercase tracking-wider mb-2">
                        <Calendar size={14} />
                        AVAILABILITY
                      </div>
                      <div className="text-sm text-white">
                        {tutor.availability}
                      </div>
                    </div>

                    {/* Rate */}
                    <div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 uppercase tracking-wider mb-2">
                        <DollarSign size={14} />
                        HOURLY RATE
                      </div>
                      <div className="text-2xl font-bold text-white">
                        ${tutor.hourlyRate}
                      </div>
                    </div>
                  </div>

                  {/* Book Button */}
                  <button
                    onClick={handleBooking}
                    className="w-full mt-8 py-4 bg-white text-black hover:bg-gray-200 transition-colors uppercase text-xs tracking-widest font-semibold"
                  >
                    BOOK NOW
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TutorDetailPage;